import type { ContentPage } from "../types";

// Written because ChatGPT answered "how do I make an AI duet" without
// searching and no page explained it (24 Oct 2026 AI search audit). Every
// step is what /create does today: up to 6 voices (MAX_VOICES in
// components/modals/conversionRequest.js), "Make it a duet" and the "Often
// with" tab (components/studio/VoicePicker.tsx), Auto pick / Choose myself,
// the coloured bar, drag, play, split, remove and the 2.5 s minimum
// (components/studio/WhoSings.tsx, SHORTEST_STRETCH_SECS), pitch per voice
// (SoundOptions.tsx), and the Marble Run video at 1 coin on Creator
// (packages/ui/core/src/i18n/messages/studio.ts, studioOptions.ts). Update
// this page when any of those change.
const page: ContentPage = {
  section: "blog",
  slug: "how-to-make-an-ai-duet",
  coverLabel: "AI duets",
  title: "How to Make an AI Duet With 2 to 6 Voices (2026 Guide)",
  metaDescription:
    "Make an AI duet step by step: pick two to six voices, let Jammable split the parts or choose who sings each line, set each voice's pitch and share a video.",
  h1: "How to make an AI duet with two or more voices",
  kind: "Guide",
  stats: [
    { value: "Up to 6", label: "voices on one Jammable track" },
    { value: "2.5 s", label: "shortest part a voice can sing" },
    { value: "±24", label: "semitones of pitch for each voice" },
  ],
  answer:
    "To make an AI duet, open the Jammable cover maker, add a song or a recording, pick a voice and press Make it a duet to add more, up to 6. Leave Auto pick on and the voices take turns where the singing changes, or choose Choose myself to decide who sings each part. Set each voice's pitch, then create.",
  takeaways: [
    "An AI duet is one song sung by two or more AI voices, each taking its own parts.",
    "Auto pick hands each part to the next voice wherever the singing changes; Choose myself lets you drag where the voices swap.",
    "Every voice has its own pitch setting, so a low voice and a high voice can share a song.",
    "Duets are covers like any other: unlimited on every plan, and the Creator plan can turn one into a Marble Run video.",
  ],
  queries: [
    "ai duet",
    "how to make an ai duet",
    "ai duet generator",
    "ai cover with two voices",
    "ai cover multiple voices",
    "make two ai voices sing together",
    "ai duet maker",
  ],
  published: "2026-10-24",
  updated: "2026-10-24",
  blocks: [
    {
      type: "md",
      text: `## What is an AI duet?

An AI duet is a cover where two or more AI voices share one song. Each voice takes its own parts: one sings the verse and another the chorus, or they trade lines. The music stays the same; only who is singing changes from part to part.

Under the hood it is the same voice conversion as a normal [AI cover](/blog/ai-singing-voice-generator). Jammable separates the vocal from the music, converts each part into the voice you gave it, and mixes the parts back over the music as one track. Jammable lets you put up to 6 voices on one track, which covers duets, trios and full group numbers.

## What you need

- **A song file or a recording.** An MP3, WAV or M4A of up to 6 minutes, a recording you make in the browser, or a song already in your Library. Use your own recording or a track you have the rights to.
- **Two or more voices.** Any of the 60,000+ community voices in the [voice library](/models), or a voice you trained from your own singing.
- **A plan.** You can set a duet up on a free account, but making it needs a plan. Every plan has unlimited covers, duets included, from $1.99 for the first month.

It all runs in the browser on a phone or a computer, with nothing to install.`,
    },
    {
      type: "steps",
      heading: "How to make an AI duet on Jammable, step by step",
      items: [
        {
          title: "Open the cover maker and add the song",
          body: "Go to [Create](/create) and upload a file, press Record, or pick a song from your Library. If you want to choose who sings each part yourself, it has to be one of those three: a song Jammable can't play in the browser is left to Auto pick.",
        },
        {
          title: "Pick the first voice",
          body: "Search by name or browse Trending, Recently used, My voices or a category such as Music, Anime, Cartoon or Gaming. Each voice shows how many times it has been used.",
        },
        {
          title: "Press Make it a duet",
          body: "Add a second voice, and more if you like, up to 6. The *Often with* tab shows voices people paired with your first pick this week, which is a quick way to find a pairing that works.",
        },
        {
          title: "Decide who sings when",
          body: "Open *Who sings when*. Leave *Auto pick* on and the voices take turns wherever the singing changes, or choose *Choose myself* to draw the parts yourself (the next section shows how).",
        },
        {
          title: "Set each voice's pitch",
          body: "In *Sound*, every voice has its own pitch, from -24 to +24 semitones. A female voice over a male original usually wants +12; a male voice over a female original, -12. A shift of 12 keeps the vocal in the song's key.",
        },
        {
          title: "Create and listen",
          body: "Jammable converts each part and mixes the duet back over the music. It is saved in your [Library](/library). Not happy with a swap? Change the parts and make it again: covers are unlimited.",
        },
      ],
    },
    {
      type: "md",
      text: `## Auto pick or Choose myself?

**Auto pick** is the default. Jammable finds where the singing changes and hands each part to the next voice in turn. It is the fastest way to get a duet, and it works on every song, including ones you can't edit in the browser.

**Choose myself** gives you the whole song as one tall bar, each part coloured by the voice that sings it. Jammable starts you off with the voices taking turns, a few parts each. From there:

- **Drag a border** to move where the voices swap. Play the song to hear where each line starts.
- **Tap a part** to select it. Its panel plays just that part, so you can check it.
- **Give it to another voice** with *Sung by*.
- **Split it** into two parts, so one voice can take the first half of a line and another the second.
- **Remove it** and choose whether the part before or the part after takes it over.

Neighbouring parts sung by the same voice join into one. Each part needs at least 2.5 seconds, because anything shorter leaves the voice nothing to sing; Jammable tells you if a part is too short.

Use Auto pick for a quick result or a song with clear back-and-forth lines. Use Choose myself when you know exactly who should take the verse, the chorus and the bridge.`,
    },
    {
      type: "callout",
      tone: "tip",
      title: "One voice at a time",
      text: "Voice conversion turns one voice into another well and a stack of voices badly. Give each part to one voice rather than trying to make two voices sing the same line at once. If the original has harmonies or ad-libs, turn on *Split out backing vocals* in Sound for a cleaner lead.",
    },
    {
      type: "md",
      text: `## Duet ideas that work

- **Call and response.** Songs that already go back and forth between two singers are the easiest: Auto pick usually finds the swaps on its own.
- **Verse and chorus.** One voice on the verses, another on every chorus. With Choose myself, drag the borders to the start of each chorus.
- **Low and high.** Pair a deep voice with a high one and set their pitch separately, so each sings in a range that suits it.
- **Duet with yourself.** Train a voice from your own singing and put it next to any voice in the library. You can also stack your own voice twice at different pitches.
- **The whole cast.** Up to 6 voices means a group number where every character gets a line. Keep each part at least a few seconds long so every voice is heard.

## Duet with your own voice

The voice you duet with can be yours. Train a voice on [Train](/train) from at least 3 minutes of clean singing or speech in one voice (6 to 10 minutes sounds better); a 10-minute dataset is usually ready in about 25 minutes. It then shows under *My voices* in the voice picker, ready to pair with any other. Training comes with the Creator plan ($9.99 for the first month, then $24.99 a month) and the Power plan, or a training pass. Train on your own voice, or on a voice whose owner has agreed to it. Full steps: [how to train your own AI voice model](/blog/how-to-train-an-ai-voice-model).

## Turn your duet into a video

On the Creator and Power plans, Jammable can turn a duet into a **Marble Run** video: your voices race as marbles, and whoever leads, sings. Lead changes become voice changes, with the lyrics on screen, ready to post on TikTok and Reels. Pick a look (Midnight, Neon, Ocean, Royal, Lava or Gold) or customise the marbles, sky and lyrics font. Each video costs 1 coin; Creator includes 50 video coins a month and Power has unlimited coins.`,
    },
    {
      type: "md",
      text: `## Why does my AI duet sound wrong?

- **Both voices sound alike.** Pick voices with clearly different tones, or shift one voice's pitch by 12 semitones.
- **A voice strains on high or low notes.** That voice is singing outside the range it was trained on. Change its pitch on its own, without touching the other voice.
- **The swap lands mid-word.** Switch to Choose myself and drag the border to the gap between lines.
- **"Each part needs at least 2.5 seconds."** A part is too short. Drag its borders wider, or remove it and let the part before or after take it over.
- **Choose myself isn't offered.** The song isn't one Jammable can play in the browser. Upload the file, record it, or use a song from your Library, or leave it on Auto pick.
- **It sounds robotic.** Usually strong autotune or the wrong pitch. [Why does my AI cover sound robotic](/blog/why-does-my-ai-cover-sound-robotic) goes through each fix.`,
    },
    {
      type: "cta",
      href: "/create",
      label: "Make a duet",
      text: "Up to 6 voices on one track, with unlimited covers on every plan.",
    },
  ],
  faqs: [
    {
      q: "How do I make an AI duet?",
      a: "On Jammable, open Create, add a song or a recording, pick a voice and press Make it a duet to add more. Leave Auto pick on or choose who sings each part yourself, set each voice's pitch and create.",
    },
    {
      q: "How many voices can sing on one AI cover?",
      a: "Up to 6 on Jammable.",
    },
    {
      q: "Can I choose which voice sings which part?",
      a: "Yes. Choose myself shows the song as one bar coloured by voice: drag the borders to move where the voices swap, and tap a part to play it, give it to another voice, split it or remove it. It needs an uploaded file, a recording or a song from your Library.",
    },
    {
      q: "Can two AI voices sing the same line at once?",
      a: "Each part is sung by one voice. Voice conversion handles one voice at a time well, so Jammable gives each part to a single voice.",
    },
    {
      q: "Do AI duets cost extra?",
      a: "No. A duet is a cover, and every plan has unlimited covers. Turning a duet into a Marble Run video costs 1 video coin on Creator or Power.",
    },
    {
      q: "Can I make a duet with my own voice?",
      a: "Yes. Train a voice from at least 3 minutes of your singing, then pick it under My voices next to any other voice.",
    },
    {
      q: "Does it work on a phone?",
      a: "Yes. The cover maker runs in the browser on phones and computers, and you can record straight into it.",
    },
  ],
  related: [
    {
      label: "AI singing voice generator",
      href: "/blog/ai-singing-voice-generator",
    },
    {
      label: "Why does my AI cover sound robotic?",
      href: "/blog/why-does-my-ai-cover-sound-robotic",
    },
    {
      label: "How to train your own AI voice model",
      href: "/blog/how-to-train-an-ai-voice-model",
    },
    {
      label: "Best AI cover generators",
      href: "/compare/best-ai-cover-generators",
    },
  ],
};

export default page;
